import React, { useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'hooks/useTranslation';
import { useQueryParams } from 'hooks';
import { Input, Button } from 'components/shared';
import { InputSize } from 'helpers/constants';
import { trackingSearchClear } from 'services/analytics';
import Icon from 'assets/icons';
import styles from './SearchBar.module.scss';
import classnames from 'classnames';

interface SearchBarProps {
  /** @function - Allows the parent component to call a fn when the search is cleared */
  onClearClick?: () => void;
  className?: string;
  size?: InputSize;
}

export const SearchBar: React.FC<SearchBarProps> = ({ onClearClick, className, size }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const isSearchRoute = useRouteMatch({ path: '/search' });
  let queryParams = useQueryParams();

  const [query, setQuery] = React.useState('');
  const [isClearVisible, setClearVisibility] = React.useState(false);

  /**
   * If visiting search route directly from url
   * populate search box with term from url
   */
  useEffect(() => {
    if (isSearchRoute) {
      const term = queryParams.get('term');
      setQuery(term as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Clear search box
   * if route changed after searching
   */
  useEffect(() => {
    if (!isSearchRoute) {
      setQuery('');
      setClearVisibility(false);
    }
  }, [isSearchRoute]);

  /**
   * Opens the search page with the input text
   * used as the query string value
   */
  const onSearchSubmit = React.useCallback(
    (query: string) => {
      setClearVisibility(true);
      history.push(`/search/qs?term=${encodeURIComponent(query)}`);
    },
    [history]
  );

  /**
   * Clears the search and optionally calls a function
   * so we can also trigger an action on the parent if needed
   */
  const onSearchClear = React.useCallback(() => {
    setQuery('');
    setClearVisibility(false);
    onClearClick && onClearClick();
    history.push('/');
    // TODO: Analytics event missing - Check SearchBar.js on V3 - #24
    trackingSearchClear();
  }, [history, onClearClick]);

  return (
    <div className={classnames(className, styles.searchBar, styles.container)}>
      <div className={styles.inputResults}>
        <Input
          type="search"
          icon={<Icon.Search />}
          size={size}
          value={query}
          placeholder={t('navigation-bar.search.placeholder')}
          theme="Search"
          maxLength={512}
          onChange={(e) => setQuery(e.target.value)}
          onSubmit={(value) => {
            if (value.trim() !== '') onSearchSubmit(value);
          }}
        >
          {isClearVisible && (
            <div className={styles.clear}>
              <Button onClick={onSearchClear} className={styles.clearLink} type="Chiclet">
                {t('navigation-bar.search.clear-label')}
              </Button>
            </div>
          )}
        </Input>
      </div>
    </div>
  );
};

export default SearchBar;
